"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronRight,
  MessageCircle,
  Minus,
  RotateCcw,
  Shuffle,
  Spade,
  Target,
  X,
} from "lucide-react";
import { useSessionStore, interleaveScenes } from "@/lib/session-store";
import type { SessionFilters, SessionMode, SessionResult } from "@/lib/session-store";
import type { PilotScene } from "@/lib/scene-types";
import type { FsrsGrade } from "@/lib/fsrs";
import {
  reviewConcept,
  buildConceptKey,
  getDueConcepts,
  getFsrsCard,
  getFsrsSnapshot,
  subscribeFsrs,
} from "@/lib/fsrs";
import { saveAnswer, loadProgress } from "@/lib/progress";
import {
  EMPTY_TRAIN_LOG,
  getTrainLogSnapshot,
  logSession,
  markDay,
  subscribeTrainLog,
} from "@/lib/train-log";
import { PokerTable } from "@/components/poker-table";
import { SpotPrompt, seededShuffle } from "@/components/spot-prompt";
import { gradeDifficulty } from "@/lib/scene-difficulty";
import { isTrainPoolScene } from "@/lib/scene-trainable";
import { handZh } from "@/lib/poker-cards";
import { jargonZh } from "@/lib/jargon-zh";
import {
  positionZh,
  conceptZh,
  actionZh,
  ACTION_EXPLAIN,
} from "@/lib/poker-terms-zh";
import { teachText } from "@/lib/teach-text";
import { cn } from "@/lib/cn";
import { DISPLAY_FONT, EASE_OUT } from "@/components/dojo-theme";
import { DojoFrame as BaseDojoFrame, FreqBar169, HandMatrix169 } from "@/components/dojo";
import { TodayDojoHero } from "@/components/train-dojo-hero";
import { MemoryGrowth } from "@/components/train-memory-growth";

/** 决策计时阈值(ms):≤EASY 易 / >HARD 难 / >CAP 污染按 good 且不计均速 */
export const EASY_MS = 8000;
export const HARD_MS = 25000;
export const CAP_MS = 120000;

/** 到期复习封顶:每个到期概念取代表场景数 / 单次复习总量上限 */
export const REVIEW_CAP = 20;
export const PER_CONCEPT = 2;

function wallNow(): number {
  return Date.now();
}

export function gradeFromAnswer(correct: boolean, timeMs: number): FsrsGrade {
  if (!correct) return "again";
  if (timeMs > CAP_MS) return "good";
  if (timeMs <= EASY_MS) return "easy";
  if (timeMs > HARD_MS) return "hard";
  return "good";
}

// ─── 设计基调:灯下道场 ────────────────────────────────────────────────────────
// 绿炭毡底 + 头顶一盏暖金灯 + 牌桌主视觉(局面空间化:你坐这/对手坐那/牌面在中间)。
// 翡翠 = 主行动;金 = 奖励(答对/连对/到期复习)。
// DISPLAY_FONT / EASE_OUT 已迁入 @/components/dojo-theme 共享。

function DojoFrame({ children }: { children: React.ReactNode }) {
  return <BaseDojoFrame width="max-w-3xl">{children}</BaseDojoFrame>;
}

// ─── Setup Phase ─────────────────────────────────────────────────────────────

const MODE_INFO: Record<
  SessionMode,
  { label: string; desc: string; icon: React.ComponentType<{ className?: string }> }
> = {
  drill: { label: "专项训练", desc: "按街、位置或场景筛选,集中攻克一个弱点", icon: Target },
  mixed: { label: "混合训练", desc: "各种局面交错出现,模拟实战的决策节奏", icon: Shuffle },
  review: { label: "复习", desc: "重做之前做错的题,把漏洞补成记忆", icon: RotateCcw },
};

const STREET_OPTIONS = [
  { value: "preflop", label: "翻前" },
  { value: "flop", label: "翻牌" },
  { value: "turn", label: "转牌" },
  { value: "river", label: "河牌" },
  { value: "multi", label: "多街" },
];

const LEVEL_OPTIONS = [
  { value: "beginner", label: "入门" },
  { value: "intermediate", label: "进阶" },
  { value: "advanced", label: "高阶" },
];

function SetupPhase({
  facets,
  allScenes,
  dueIds,
  onStart,
  onStartDue,
}: {
  facets: { scenarios: string[]; positions: string[] };
  allScenes: PilotScene[];
  dueIds: string[];
  onStart: (
    mode: SessionMode,
    filters: SessionFilters,
    count: number,
    seedSceneId?: string,
  ) => void;
  onStartDue: (seedSceneId?: string) => void;
}) {
  const searchParams = useSearchParams();
  // deep-link 预置首题:/train?scene=X 开新会话时把该场景放队首(续练会话不受影响)
  const seedScene = searchParams.get("scene") || undefined;
  const [mode, setMode] = useState<SessionMode>(
    (searchParams.get("mode") as SessionMode) || "mixed",
  );
  const [street, setStreet] = useState(searchParams.get("street") || "");
  const [scenario, setScenario] = useState(searchParams.get("scenario") || "");
  const [position, setPosition] = useState(searchParams.get("position") || "");
  const [level, setLevel] = useState(searchParams.get("level") || "beginner");
  const [count, setCount] = useState(10);
  const reduce = useReducedMotion();
  // 导语仅首访(从未打卡)显示;老用户直接进 hero
  const trainLog = useSyncExternalStore(
    subscribeTrainLog,
    getTrainLogSnapshot,
    () => EMPTY_TRAIN_LOG,
  );
  const isFirstVisit = trainLog.days.length === 0;

  const levelCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let usable = 0;
    for (const s of allScenes) {
      if (!isTrainPoolScene(s)) continue;
      usable += 1;
      // 读时派生决策难度(不再用视频级 level 标签)
      const lv = gradeDifficulty(s);
      counts[lv] = (counts[lv] ?? 0) + 1;
    }
    return { byLevel: counts, usable };
  }, [allScenes]);

  const availableCount = useMemo(() => {
    return allScenes.filter((s) => {
      if (!isTrainPoolScene(s)) return false;
      if (street && s.spot.street !== street) return false;
      if (scenario && !(s.concepts?.scenarios || []).includes(scenario)) return false;
      if (position && !(s.concepts?.positions || []).includes(position)) return false;
      if (level && gradeDifficulty(s) !== level) return false;
      return true;
    }).length;
  }, [allScenes, street, scenario, position, level]);

  const list: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduce ? 0 : 0.07,
        delayChildren: reduce ? 0 : 0.05,
      },
    },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0.2 } : { duration: 0.5, ease: EASE_OUT },
    },
  };

  const selectCls =
    "rounded-lg border border-[#26372C] bg-[#0C130F] px-2.5 py-2 text-sm text-stone-200 outline-none transition focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20";

  return (
    <motion.div
      className="space-y-8"
      variants={list}
      initial="hidden"
      animate="visible"
    >
      <motion.header variants={item} className="space-y-3">
        <p className="text-[11px] font-semibold tracking-[0.24em] text-amber-300/70">
          GTO DOJO
        </p>
        <h1
          className={cn(
            DISPLAY_FONT,
            "text-4xl font-extrabold tracking-tight text-stone-50 md:text-5xl",
          )}
        >
          训练道场
        </h1>
        {isFirstVisit && (
          <p className="max-w-[52ch] text-sm leading-relaxed text-stone-400">
            不懂术语也能上手:看题、凭直觉选一个打法、马上看讲解。一组练完,手感就长一分。
          </p>
        )}
      </motion.header>

      {/* 今日道场:到期数 + 本周点阵 + 一键开始(due=0 → 空态「第 N 天 · 先练 10 题打底」) */}
      <motion.div variants={item}>
        <TodayDojoHero
          dueCount={dueIds.length}
          onStartDue={() => onStartDue(seedScene)}
          onStartFresh={() =>
            onStart(
              "mixed",
              (levelCounts.byLevel["beginner"] ?? 0) > 0 ? { level: "beginner" } : {},
              10,
              seedScene,
            )
          }
        />
      </motion.div>

      {/* 模式选择 */}
      <motion.div variants={item} className="grid gap-3 sm:grid-cols-3">
        {(Object.entries(MODE_INFO) as [SessionMode, typeof MODE_INFO[SessionMode]][]).map(
          ([key, info]) => {
            const Icon = info.icon;
            const active = mode === key;
            return (
              <motion.button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                whileHover={reduce ? undefined : { y: -3 }}
                whileTap={reduce ? undefined : { scale: 0.98 }}
                className={cn(
                  "rounded-2xl border p-4 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]",
                  active
                    ? "border-emerald-400/60 bg-[#12241A] shadow-[0_12px_36px_-16px_rgba(52,211,153,0.35)]"
                    : "border-[#223329] bg-[#0E1712]/80 hover:border-[#35503E]",
                )}
              >
                <div
                  className={cn(
                    "flex size-9 items-center justify-center rounded-lg transition-colors",
                    active
                      ? "bg-emerald-400/15 text-emerald-300"
                      : "bg-[#16221B] text-stone-500",
                  )}
                >
                  <Icon className="size-4.5" />
                </div>
                <div className="mt-3 text-sm font-semibold text-stone-100">
                  {info.label}
                </div>
                <div className="mt-1 text-xs leading-relaxed text-stone-500">
                  {info.desc}
                </div>
              </motion.button>
            );
          },
        )}
      </motion.div>

      {/* 难度筛选(默认入门,新手友好路径常驻可见) */}
      <motion.div
        variants={item}
        className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl border border-[#223329] bg-[#0D1510]/80 px-5 py-4"
      >
        <label className="flex items-center gap-2.5 text-xs font-medium text-stone-500">
          难度
          <select value={level} onChange={(e) => setLevel(e.target.value)} className={selectCls}>
            <option value="">全部({levelCounts.usable})</option>
            {LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}({levelCounts.byLevel[o.value] ?? 0})
              </option>
            ))}
          </select>
        </label>
      </motion.div>

      {/* 专项筛选 */}
      {mode === "drill" && (
        <motion.div
          variants={item}
          className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl border border-[#223329] bg-[#0D1510]/80 px-5 py-4"
        >
          <label className="flex items-center gap-2.5 text-xs font-medium text-stone-500">
            街
            <select value={street} onChange={(e) => setStreet(e.target.value)} className={selectCls}>
              <option value="">全部</option>
              {STREET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2.5 text-xs font-medium text-stone-500">
            场景
            <select value={scenario} onChange={(e) => setScenario(e.target.value)} className={selectCls}>
              <option value="">全部</option>
              {facets.scenarios.map((s) => (
                <option key={s} value={s}>
                  {conceptZh(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2.5 text-xs font-medium text-stone-500">
            位置
            <select value={position} onChange={(e) => setPosition(e.target.value)} className={selectCls}>
              <option value="">全部</option>
              {facets.positions.map((p) => (
                <option key={p} value={p}>
                  {positionZh(p)}
                </option>
              ))}
            </select>
          </label>
        </motion.div>
      )}

      {/* 题量 */}
      <motion.div variants={item} className="flex flex-wrap items-center gap-4">
        <span className="text-xs font-medium text-stone-500">这一组练几题?</span>
        <div className="inline-flex rounded-xl border border-[#223329] bg-[#0C130F] p-1">
          {[5, 10, 15, 20].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setCount(n)}
              className={cn(
                "rounded-lg px-4 py-1.5 font-mono text-sm tabular-nums transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 active:scale-[0.96]",
                count === n
                  ? "bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/40"
                  : "text-stone-500 hover:text-stone-300",
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </motion.div>

      {/* 开始 */}
      <motion.div variants={item} className="flex items-center gap-4">
        <button
          type="button"
          disabled={availableCount === 0}
          onClick={() =>
            onStart(mode, { street: street || undefined, scenario: scenario || undefined, position: position || undefined, level: level || undefined }, count, seedScene)
          }
          className={cn(
            "group inline-flex items-center gap-2 rounded-xl px-7 py-3 text-sm font-bold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]",
            availableCount === 0
              ? "cursor-not-allowed bg-[#182119] text-stone-600"
              : "bg-emerald-400 text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] hover:bg-emerald-300 active:scale-[0.98]",
          )}
        >
          开始训练
          <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
        <span
          className={cn(
            "font-mono text-xs tabular-nums",
            availableCount === 0 ? "text-rose-300" : "text-stone-500",
          )}
        >
          {availableCount === 0
            ? "这个组合暂时没有可出的题,换个筛选试试"
            : `牌库可用 ${availableCount} 题`}
        </span>
      </motion.div>
    </motion.div>
  );
}

// ─── Active Phase ────────────────────────────────────────────────────────────
// 出题面(牌桌 + 标题 + 提示 + 选项)已抽至 <SpotPrompt>(@/components/spot-prompt),
// 判答/揭晓/反馈(领地条、尺度条、频率点、推荐打法、为什么、下一题)留在 ActivePhase。

/** 尺度 → 底池比例;overbet 按满池渲染(比例条画不下超池) */
const SIZING_FRAC: Record<string, number> = {
  "1/3": 1 / 3,
  "1/2": 1 / 2,
  "2/3": 2 / 3,
  "3/4": 3 / 4,
  pot: 1,
  overbet: 1,
};

/** 频率 → 强度点 + 中文标签(紧凑视觉提示,不成句) */
const FREQ_CUE: Record<string, { dots: number; tag: string }> = {
  always: { dots: 4, tag: "每次都打" },
  high: { dots: 3, tag: "高频" },
  mixed: { dots: 2, tag: "混合" },
  low: { dots: 1, tag: "少见" },
  conditional: { dots: 2, tag: "看对手" },
};

function sizingZh(sizing: string): string {
  if (sizing === "pot") return "满池";
  if (sizing === "overbet") return "超池";
  return `${sizing} 池`;
}

/** 刷新续练回填:若当前场景已有作答记录,按 chosenLabel 反查选项 id,直接呈揭晓态 */
function backfillChosen(
  scene: PilotScene | undefined,
  results: SessionResult[],
): string | null {
  if (!scene?.decision?.parsed) return null;
  const existing = results.find((r) => r.sceneId === scene.scene_id);
  if (!existing) return null;
  const opts = seededShuffle(scene.decision.options, scene.scene_id);
  return opts.find((o) => o.label === existing.chosenLabel)?.id ?? null;
}

function ActivePhase({
  scenes,
  currentIndex,
  results,
  onAnswer,
  onNext,
  onQuit,
}: {
  scenes: PilotScene[];
  currentIndex: number;
  results: SessionResult[];
  onAnswer: (correct: boolean, timeMs: number, chosenLabel: string) => void;
  onNext: () => void;
  onQuit: () => void;
}) {
  const scene = scenes[currentIndex];
  const [chosen, setChosen] = useState<string | null>(() =>
    backfillChosen(scenes[currentIndex], results),
  );
  const [prevIndex, setPrevIndex] = useState(currentIndex);
  const shownAtRef = useRef(0);
  const reduce = useReducedMotion();

  if (prevIndex !== currentIndex) {
    setPrevIndex(currentIndex);
    setChosen(backfillChosen(scenes[currentIndex], results));
  }

  useEffect(() => {
    shownAtRef.current = wallNow();
  }, [currentIndex]);

  if (!scene) return null;

  const { spot, decision } = scene;
  const useStructured = decision?.parsed === true && decision.options.length >= 2;

  const options = seededShuffle(decision!.options, scene.scene_id);
  const correctIds = new Set(decision!.correct_ids);

  const revealed = chosen !== null;
  const isCorrect = chosen ? correctIds.has(chosen) : false;

  function handleChoose(optId: string) {
    if (chosen) return;
    setChosen(optId);
    const label = options.find((o) => o.id === optId)?.label ?? optId;
    const started = shownAtRef.current;
    const elapsed = started > 0 ? Math.max(0, wallNow() - started) : 0;
    onAnswer(correctIds.has(optId), elapsed, label);
  }

  const correctCount = results.filter((r) => r.correct).length;
  const chosenOpt = chosen ? options.find((o) => o.id === chosen) : undefined;

  const sizing = decision!.sizing;
  const sizingFrac = sizing ? SIZING_FRAC[sizing] : undefined;
  const showSizingBar =
    useStructured &&
    sizingFrac != null &&
    (decision!.primary_action === "bet" || decision!.primary_action === "raise");
  const chosenSizing = chosenOpt?.sizing ?? null;
  const chosenFrac = chosenSizing ? SIZING_FRAC[chosenSizing] : undefined;
  const showChosenTick = showSizingBar && chosenFrac != null && chosenSizing !== sizing;
  const sizingLabel = sizing
    ? sizing === "pot" || sizing === "overbet"
      ? `${sizingZh(sizing)}${actionZh(decision!.primary_action)}`
      : `${actionZh(decision!.primary_action)} ${sizingZh(sizing)}`
    : "";
  const freqCue = useStructured ? FREQ_CUE[decision!.frequency] : undefined;

  const correctOpts = useStructured
    ? decision!.options.filter((o) => correctIds.has(o.id))
    : [];
  const gist =
    correctOpts.length > 1
      ? `这题不止一种正解:${correctOpts.map((o) => o.label).join(" / ")}。`
      : correctOpts.length === 1
        ? `推荐「${correctOpts[0].label}」:${ACTION_EXPLAIN[correctOpts[0].action] ?? "这是当前局面的最优解"}。`
        : "这题是研习卡,没有唯一正解,看完整讲解。";

  return (
    <div className="decision-active space-y-5">
      {/* 进度:金灯下的条 + 等宽数字 */}
      <div className="flex items-center gap-4">
        <span className="shrink-0 font-mono text-xs tabular-nums text-stone-400">
          第 {currentIndex + 1} 题
          <span className="text-stone-600"> / {scenes.length}</span>
        </span>
        <div
          className="h-1 flex-1 overflow-hidden rounded-full bg-[#1A271E]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={scenes.length}
          aria-valuenow={currentIndex + 1}
        >
          <motion.div
            className="h-full w-full origin-left rounded-full bg-emerald-400"
            initial={false}
            animate={{ scaleX: (currentIndex + 1) / scenes.length }}
            transition={
              reduce ? { duration: 0 } : { type: "spring", stiffness: 140, damping: 22 }
            }
          />
        </div>
        <span className="shrink-0 font-mono text-xs tabular-nums text-amber-300/90">
          ✓ {correctCount}
        </span>
      </div>

      {/* 出题面:牌桌 + 标题 + 新手提示 + 动作选项(抽至 SpotPrompt,home 首屏共用) */}
      <SpotPrompt scene={scene} chosen={chosen} onChoose={handleChoose} />

      {/* 反馈 */}
      {revealed && (
        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="space-y-4"
          aria-live="polite"
        >
          <div
            className={cn(
              "rounded-2xl border p-5",
              isCorrect
                ? "border-amber-300/35 bg-[#1B1508]/90 shadow-[0_0_70px_-18px_rgba(227,181,91,0.4)]"
                : "border-rose-400/30 bg-[#1E1013]/90",
            )}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                className={cn(
                  "flex size-7 shrink-0 items-center justify-center rounded-full",
                  isCorrect
                    ? "bg-amber-300/15 text-amber-300"
                    : "bg-rose-400/15 text-rose-300",
                )}
              >
                {isCorrect ? <Check className="size-3.5" /> : <X className="size-3.5" />}
              </span>
              <span
                className={cn(
                  "text-sm font-bold",
                  isCorrect ? "text-amber-200" : "text-rose-200",
                )}
              >
                {isCorrect ? "回答正确" : "这题没选对"}
              </span>
              <span className="text-xs text-stone-500">
                {isCorrect ? "好手感,继续保持。" : "错题是最值钱的一课。"}
              </span>
            </div>

            {useStructured && (
              <div className="mt-4 flex divide-x divide-[#1A271E] overflow-hidden rounded-xl border border-[#223329] bg-[#0B120E]">
                {decision!.options.map((opt, i) => {
                  const isChosenTerritory = chosen === opt.id;
                  const isGtoTerritory = correctIds.has(opt.id);
                  return (
                    <motion.div
                      key={opt.id}
                      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={
                        reduce
                          ? { duration: 0.2 }
                          : { duration: 0.25, delay: 0.05 + i * 0.03, ease: EASE_OUT }
                      }
                      className={cn(
                        "relative flex-1 px-1.5 py-3 text-center",
                        isChosenTerritory && isGtoTerritory
                          ? "bg-emerald-400/10 text-emerald-100 ring-2 ring-inset ring-emerald-400/70"
                          : isGtoTerritory
                            ? "bg-[#261E0D] text-amber-100 ring-1 ring-inset ring-amber-300/60"
                            : isChosenTerritory
                              ? "bg-[#271114] text-rose-100 ring-2 ring-inset ring-rose-400/60"
                              : "text-stone-500",
                      )}
                    >
                      {isGtoTerritory && !isChosenTerritory && (
                        <motion.span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_90%_at_50%_40%,rgba(227,181,91,0.22),transparent)]"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={
                            reduce ? { duration: 0 } : { duration: 0.28, delay: 0.15 }
                          }
                        />
                      )}
                      <div className="relative flex h-4 items-center justify-center gap-1">
                        {isChosenTerritory && (
                          <motion.span
                            className="flex h-4 items-center rounded-full bg-[#F0E9D2] px-1.5 text-[9px] font-bold leading-none text-[#3A3428] ring-1 ring-black/40"
                            initial={reduce ? false : { opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={
                              reduce
                                ? { duration: 0 }
                                : { type: "spring", stiffness: 300, damping: 20, delay: 0.14 }
                            }
                          >
                            你
                          </motion.span>
                        )}
                        {isGtoTerritory && (
                          <motion.span
                            className="flex size-4 items-center justify-center rounded-full bg-amber-300 text-[#3A2A05]"
                            initial={reduce ? false : { opacity: 0, scale: 0.4 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={
                              reduce
                                ? { duration: 0 }
                                : { type: "spring", stiffness: 320, damping: 18, delay: 0.16 }
                            }
                          >
                            <Check className="size-2.5" strokeWidth={3} />
                          </motion.span>
                        )}
                      </div>
                      <div className="relative mt-1.5 flex h-7 items-center justify-center">
                        {opt.sizing ? (
                          <span
                            className={cn(
                              DISPLAY_FONT,
                              "text-xl font-extrabold tracking-tight",
                            )}
                          >
                            {opt.sizing}
                            <span className="ml-0.5 text-[10px] font-medium opacity-70">
                              池
                            </span>
                          </span>
                        ) : (
                          <span className="text-sm font-semibold">
                            {actionZh(opt.action)}
                          </span>
                        )}
                      </div>
                      <div className="relative mt-0.5 h-3.5 text-[10px] leading-none opacity-60">
                        {opt.sizing ? actionZh(opt.action) : ""}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* 起手牌全图(#dab5):揭晓态把 spot.hero_hand 放回 169 格全域;hero_hand
                缺失/不可解析时组件自身返回 null,不渲染(禁编造) */}
            <HandMatrix169 heroHand={spot.hero_hand} className="mt-5" />

            {/* 起手强度频率条(#937b):1326 组合五层堆叠 + 金色刻度 = 你这手百分位 */}
            <FreqBar169 heroHand={spot.hero_hand} className="mt-3" />

            {showSizingBar && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] font-medium tracking-wider text-stone-500">
                    推荐尺度
                  </span>
                  <span
                    className={cn(
                      DISPLAY_FONT,
                      "text-sm font-bold tracking-tight text-amber-200",
                    )}
                  >
                    {sizingLabel}
                  </span>
                </div>
                <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-full bg-[#131E17] ring-1 ring-inset ring-[#223329]">
                  {[0.25, 0.5, 0.75].map((t) => (
                    <span
                      key={t}
                      aria-hidden
                      className="absolute inset-y-0 w-px bg-white/[0.07]"
                      style={{ left: `${t * 100}%` }}
                    />
                  ))}
                  <motion.div
                    className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-amber-300/90"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: sizingFrac ?? 0 }}
                    transition={
                      reduce
                        ? { duration: 0 }
                        : { duration: 0.32, delay: 0.12, ease: EASE_OUT }
                    }
                  />
                  {showChosenTick && (
                    <motion.span
                      aria-hidden
                      className="absolute inset-y-0 w-[3px] rounded-full bg-rose-400"
                      style={{ left: `calc(${(chosenFrac ?? 0) * 100}% - 1px)` }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={reduce ? { duration: 0 } : { duration: 0.15, delay: 0.3 }}
                    />
                  )}
                </div>
                {showChosenTick && chosenSizing && (
                  <div className="mt-1 text-[10px] text-rose-300/70">
                    你选了{actionZh(chosenOpt?.action)} {sizingZh(chosenSizing)}
                  </div>
                )}
              </div>
            )}

            {freqCue && (
              <div className="mt-4 flex items-center gap-2.5">
                <span className="text-[11px] font-medium tracking-wider text-stone-500">
                  频率
                </span>
                <span className="flex items-center gap-1">
                  {[0, 1, 2, 3].map((d) => (
                    <motion.span
                      key={d}
                      className={cn(
                        "size-1.5 rounded-full",
                        d < freqCue.dots ? "bg-amber-300" : "bg-[#223329]",
                      )}
                      initial={reduce ? false : { opacity: 0, scale: 0.4 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={
                        reduce
                          ? { duration: 0 }
                          : { duration: 0.2, delay: 0.12 + d * 0.04, ease: EASE_OUT }
                      }
                    />
                  ))}
                </span>
                <span className="text-[11px] font-semibold text-amber-200/90">
                  {freqCue.tag}
                </span>
              </div>
            )}

            <p className="mt-4 text-sm leading-relaxed text-stone-300">{gist}</p>

            <details className="group mt-3 rounded-xl border border-white/5 bg-black/20">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-xs font-medium text-stone-400 transition-colors hover:text-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-300/60 [&::-webkit-details-marker]:hidden">
                <ChevronRight className="size-3.5 transition-transform duration-200 group-open:rotate-90" />
                为什么?看完整讲解
              </summary>
              <div className="space-y-3 border-t border-white/5 px-4 py-3.5">
                <div className="flex flex-wrap items-start gap-2.5">
                  <span className="mt-0.5 shrink-0 rounded-md bg-amber-300/15 px-2 py-0.5 text-[11px] font-bold text-amber-300">
                    推荐打法
                  </span>
                  {useStructured && decision!.primary_action !== "mixed" && (
                    <span className="mt-0.5 shrink-0 rounded-md bg-emerald-400/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
                      {actionZh(decision!.primary_action)}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 text-sm font-medium leading-relaxed text-stone-100">
                    {teachText(spot.recommendation_zh ?? spot.recommendation)}
                  </span>
                </div>
                <div className="border-t border-white/5 pt-2.5">
                  <div className="text-[11px] font-semibold tracking-wider text-stone-500">
                    为什么
                  </div>
                  <p className="mt-1.5 max-w-[65ch] text-sm leading-relaxed text-stone-400">
                    {teachText(spot.reasoning_zh ?? spot.reasoning)}
                  </p>
                </div>
              </div>
            </details>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onNext}
              className="group inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-6 py-2.5 text-sm font-bold text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] transition-all duration-150 hover:bg-emerald-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
            >
              {currentIndex + 1 >= scenes.length ? "查看成绩" : "下一题"}
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={onQuit}
              className="text-xs text-stone-500 underline-offset-4 transition hover:text-stone-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
            >
              退出训练
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ─── Done Phase (Score Card) ─────────────────────────────────────────────────

/**
 * 比上一组:本次正确率 vs train-log 倒数第二条会话。
 * 写入时序兼容:logSession 在 active→done 边沿写入,本组若已是末条(匹配 total/correct)
 * 则取倒数第二条;刷新恢复 done 时同样命中该分支。涨暖金 / 跌 stone,不红。
 */
function PrevSessionDelta({
  total,
  correct,
  currentPct,
}: {
  total: number;
  correct: number;
  currentPct: number;
}) {
  const reduce = useReducedMotion();
  const trainLog = useSyncExternalStore(
    subscribeTrainLog,
    getTrainLogSnapshot,
    () => EMPTY_TRAIN_LOG,
  );
  const prevPct = useMemo(() => {
    const sessions = trainLog.sessions;
    const last = sessions[sessions.length - 1];
    const lastIsCurrent = !!last && last.total === total && last.correct === correct;
    const prev = lastIsCurrent ? sessions[sessions.length - 2] : last;
    if (!prev || prev.total <= 0) return null;
    return Math.round((prev.correct / prev.total) * 100);
  }, [trainLog, total, correct]);

  if (prevPct === null) return null;
  const delta = currentPct - prevPct;
  const up = delta > 0;
  const flat = delta === 0;

  return (
    <motion.p
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0.2 } : { duration: 0.45, delay: 0.4, ease: EASE_OUT }}
      className="mt-3 flex items-center justify-center gap-1.5 font-mono text-sm tabular-nums"
    >
      <span className="text-stone-500">比上一组</span>
      <span
        className={cn(
          "inline-flex items-center gap-0.5 font-semibold",
          up ? "text-amber-300" : "text-stone-400",
        )}
      >
        {flat ? (
          <Minus className="size-3.5" />
        ) : up ? (
          <ArrowUpRight className="size-3.5" />
        ) : (
          <ArrowDownRight className="size-3.5" />
        )}
        {flat ? "持平" : `${up ? "+" : ""}${delta}%`}
      </span>
    </motion.p>
  );
}

function DonePhase({
  results,
  mode,
  durationMs,
  sceneById,
  onRestart,
}: {
  results: SessionResult[];
  mode: SessionMode;
  durationMs: number;
  /** sceneId→scene 现查:错题卡补牌桌+讲解,不往 SessionResult 塞 spot(避免 persist 膨胀) */
  sceneById: Map<string, PilotScene>;
  onRestart: () => void;
}) {
  const reduce = useReducedMotion();
  const total = results.length;
  const correct = results.filter((r) => r.correct).length;
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  const seconds = Math.round(durationMs / 1000);
  const gradable = results.filter((r) => r.optionCount > 0);
  const baseline = gradable.length > 0
    ? Math.round((gradable.reduce((sum, r) => sum + r.correctCount / r.optionCount, 0) / gradable.length) * 100)
    : 25;
  const delta = pct - baseline;

  const grade =
    delta >= 50 ? "S" : delta >= 35 ? "A" : delta >= 20 ? "B" : delta >= 5 ? "C" : "D";
  const gradeColor =
    delta >= 50
      ? "text-amber-300"
      : delta >= 35
        ? "text-emerald-300"
        : delta >= 20
          ? "text-stone-100"
          : "text-stone-400";

  const list: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduce ? 0 : 0.08,
        delayChildren: reduce ? 0 : 0.15,
      },
    },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0.2 } : { duration: 0.5, ease: EASE_OUT },
    },
  };

  const wrong = results.filter((r) => !r.correct);

  return (
    <motion.div
      className="space-y-8"
      variants={list}
      initial="hidden"
      animate="visible"
    >
      <header className="text-center">
        <div className="relative inline-block">
          {delta >= 50 && !reduce && (
            <motion.div
              aria-hidden
              className="absolute -inset-10 rounded-full bg-[radial-gradient(closest-side,rgba(227,181,91,0.28),transparent)]"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            />
          )}
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={
              reduce
                ? { duration: 0.25 }
                : { type: "spring", stiffness: 220, damping: 16 }
            }
            className={cn(
              DISPLAY_FONT,
              "relative text-[96px] font-extrabold leading-none tracking-tight",
              gradeColor,
            )}
          >
            {grade}
          </motion.div>
        </div>
        <motion.h1
          variants={item}
          className={cn(DISPLAY_FONT, "mt-3 text-2xl font-bold text-stone-50")}
        >
          训练完成
        </motion.h1>
        <motion.p
          variants={item}
          className="mt-2 font-mono text-xs tabular-nums text-stone-500"
        >
          {MODE_INFO[mode].label} · {total} 题 · {seconds} 秒
        </motion.p>
        <PrevSessionDelta total={total} correct={correct} currentPct={pct} />
      </header>

      {/* 战绩 */}
      <motion.div variants={item} className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-[#1E2E23] bg-[#0D1510]/80 p-5 text-center">
          <div className="font-mono text-3xl font-semibold tabular-nums text-emerald-300 md:text-4xl">
            {correct}
          </div>
          <div className="mt-1 text-[11px] font-medium tracking-wider text-stone-500">
            答对
          </div>
        </div>
        <div className="rounded-2xl border border-[#1E2E23] bg-[#0D1510]/80 p-5 text-center">
          <div className="font-mono text-3xl font-semibold tabular-nums text-rose-300/90 md:text-4xl">
            {total - correct}
          </div>
          <div className="mt-1 text-[11px] font-medium tracking-wider text-stone-500">
            答错
          </div>
        </div>
        <div className="rounded-2xl border border-[#1E2E23] bg-[#0D1510]/80 p-5 text-center">
          <div className="font-mono text-3xl font-semibold tabular-nums text-stone-50 md:text-4xl">
            {pct}%
          </div>
          <div className="mt-1 text-[11px] font-medium tracking-wider text-stone-500">
            正确率
          </div>
        </div>
      </motion.div>

      {/* 基线对比柱:你 vs 瞎猜,长度按比例,一眼看懂净优势 */}
      <motion.div
        variants={item}
        className="rounded-2xl border border-[#1E2E23] bg-[#0D1510]/80 px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <span className="w-10 shrink-0 text-right text-xs font-semibold text-stone-300">
            你
          </span>
          <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-[#131E17]">
            <motion.div
              className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.35)]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: pct / 100 }}
              transition={reduce ? { duration: 0 } : { duration: 0.55, delay: 0.35, ease: EASE_OUT }}
            />
          </div>
          <span
            className={cn(
              DISPLAY_FONT,
              "w-14 shrink-0 text-right text-xl font-extrabold tabular-nums text-stone-50",
            )}
          >
            {pct}%
          </span>
        </div>
        <div className="mt-2.5 flex items-center gap-3">
          <span className="w-10 shrink-0 text-right text-xs text-stone-500">瞎猜</span>
          <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-[#131E17]">
            <motion.div
              className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-stone-600"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: baseline / 100 }}
              transition={reduce ? { duration: 0 } : { duration: 0.55, delay: 0.42, ease: EASE_OUT }}
            />
          </div>
          <span className="w-14 shrink-0 text-right font-mono text-sm tabular-nums text-stone-500">
            {baseline}%
          </span>
        </div>
        <div className="mt-3.5 flex items-baseline justify-end gap-1.5">
          <span className="text-[11px] font-medium tracking-wider text-stone-500">净优势</span>
          <span
            className={cn(
              DISPLAY_FONT,
              "text-2xl font-extrabold tabular-nums",
              delta >= 0 ? "text-amber-300" : "text-stone-400",
            )}
          >
            {delta >= 0 ? "+" : ""}
            {delta}
          </span>
          <span className="text-[11px] text-stone-500">个百分点</span>
        </div>
      </motion.div>

      {/* 记忆成长:概念级 stability 旧→新 段条(成绩卡下、错题回顾前) */}
      <motion.div variants={item}>
        <MemoryGrowth results={results} />
      </motion.div>

      {/* 错题回顾 */}
      {wrong.length > 0 && (
        <motion.div variants={item} className="space-y-2.5">
          <h2 className="text-sm font-semibold text-stone-200">
            错题回顾
            <span className="ml-2 font-mono text-xs tabular-nums text-stone-500">
              {wrong.length}
            </span>
          </h2>
          {wrong.map((r) => {
            const scene = sceneById.get(r.sceneId);
            return (
              <div
                key={r.sceneId}
                className="rounded-xl border border-rose-400/20 bg-[#170E10]/90 px-4 py-3.5"
              >
                <div className="text-sm leading-relaxed text-stone-200">
                  {jargonZh(handZh(r.title))}
                </div>

                {scene && (
                  <div className="mt-3">
                    <PokerTable spot={scene.spot} compact />
                  </div>
                )}

                <div
                  className={cn(
                    "text-xs leading-relaxed text-stone-500",
                    scene ? "mt-3" : "mt-1.5",
                  )}
                >
                  你选了「{r.chosenLabel}」 → 推荐
                  <span className="ml-1 font-medium text-amber-300">
                    {r.correctLabel}
                  </span>
                </div>

                {scene && (scene.spot.recommendation || scene.spot.reasoning) && (
                  <details className="group mt-3 rounded-xl border border-white/5 bg-black/20">
                    <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-xs font-medium text-stone-400 transition-colors hover:text-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-300/60 [&::-webkit-details-marker]:hidden">
                      <ChevronRight className="size-3.5 transition-transform duration-200 group-open:rotate-90" />
                      为什么?看完整讲解
                    </summary>
                    <div className="space-y-3 border-t border-white/5 px-4 py-3.5">
                      <div className="flex flex-wrap items-start gap-2.5">
                        <span className="mt-0.5 shrink-0 rounded-md bg-amber-300/15 px-2 py-0.5 text-[11px] font-bold text-amber-300">
                          推荐打法
                        </span>
                        {scene.decision?.parsed &&
                          scene.decision.primary_action !== "mixed" && (
                            <span className="mt-0.5 shrink-0 rounded-md bg-emerald-400/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
                              {actionZh(scene.decision.primary_action)}
                            </span>
                          )}
                        <span className="min-w-0 flex-1 text-sm font-medium leading-relaxed text-stone-100">
                          {teachText(scene.spot.recommendation_zh ?? scene.spot.recommendation)}
                        </span>
                      </div>
                      {scene.spot.reasoning && (
                        <div className="border-t border-white/5 pt-2.5">
                          <div className="text-[11px] font-semibold tracking-wider text-stone-500">
                            为什么
                          </div>
                          <p className="mt-1.5 max-w-[65ch] text-sm leading-relaxed text-stone-400">
                            {teachText(scene.spot.reasoning_zh ?? scene.spot.reasoning)}
                          </p>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                {/* 错题即追问入口:带着这手牌去问教练 */}
                <div className="mt-3 flex justify-end">
                  <Link
                    href={`/coach?scene=${r.sceneId}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold text-emerald-200 transition hover:border-emerald-300/60 hover:bg-emerald-400/20 hover:text-emerald-100 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
                  >
                    <MessageCircle className="size-3.5" />
                    问教练:这手牌为什么这么打?
                  </Link>
                </div>
              </div>
            );
          })}
        </motion.div>
      )}

      <motion.div variants={item} className="flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={onRestart}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-6 py-2.5 text-sm font-bold text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] transition-all duration-150 hover:bg-emerald-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
        >
          <RotateCcw className="size-4" />
          再来一组
        </button>
        <Link
          href="/scenes"
          className="inline-flex items-center rounded-xl border border-[#2A3D30] px-5 py-2.5 text-sm text-stone-300 transition hover:border-emerald-300/40 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
        >
          浏览场景
        </Link>
      </motion.div>
    </motion.div>
  );
}

// ─── Page Component ──────────────────────────────────────────────────────────

type ScenesResponse = {
  total?: number;
  scenes?: PilotScene[];
  facets?: { scenarios: string[]; positions: string[]; streets: string[]; authors: string[] };
};

const FSRS_EMPTY: ReturnType<typeof getFsrsSnapshot> = {};

export default function TrainPage() {
  const [allScenes, setAllScenes] = useState<PilotScene[]>([]);
  const [facets, setFacets] = useState<{ scenarios: string[]; positions: string[] }>({
    scenarios: [],
    positions: [],
  });
  const [loading, setLoading] = useState(true);
  const reduce = useReducedMotion();
  const [dueNow] = useState(() => Date.now());

  const fsrsStore = useSyncExternalStore(
    subscribeFsrs,
    getFsrsSnapshot,
    () => FSRS_EMPTY,
  );

  const {
    phase,
    mode,
    queue,
    currentIndex,
    results,
    configure,
    start,
    recordAnswer,
    next,
    finish,
    reset,
  } = useSessionStore();

  // 仅在 active→done 边沿写会话记录;刷新恢复 done 不重写
  const prevPhaseRef = useRef<typeof phase | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // trainOnly=1:查询层主闸,概念题(parsed=false)根本不下发到 /train 客户端
        const res = await fetch("/api/scenes?trainOnly=1", { cache: "no-store" });
        const json = (await res.json()) as ScenesResponse;
        setAllScenes(json.scenes || []);
        setFacets({
          scenarios: json.facets?.scenarios || [],
          positions: json.facets?.positions || [],
        });
      } catch {
        /* empty */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (phase !== "done" || prev !== "active") return;
    if (results.length === 0) return;

    const timed = results.filter((r) => r.timeMs <= CAP_MS);
    const avgDecisionMs =
      timed.length > 0
        ? Math.round(timed.reduce((s, r) => s + r.timeMs, 0) / timed.length)
        : 0;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const date = `${y}-${m}-${d}`;

    logSession({
      mode,
      total: results.length,
      correct: results.filter((r) => r.correct).length,
      durationMs: results.reduce((s, r) => s + Math.min(r.timeMs, CAP_MS), 0),
      avgDecisionMs,
      date,
    });
    markDay(date);
  }, [phase, results, mode]);

  /** ?scene=X 预置首题校验:场景须存在且有结构化解构,否则忽略 */
  const resolveSeed = useCallback(
    (seedSceneId: string | undefined): string | null =>
      seedSceneId &&
      allScenes.some((s) => s.scene_id === seedSceneId && isTrainPoolScene(s))
        ? seedSceneId
        : null,
    [allScenes],
  );

  const handleStart = useCallback(
    (m: SessionMode, filters: SessionFilters, count: number, seedSceneId?: string) => {
      configure(m, filters, count);

      // Filter scenes
      let pool = allScenes.filter((s) => {
        // 决策池准入:结构化决策题(parsed && options>=2)且非 needs_review 标审场景
        if (!isTrainPoolScene(s)) return false;
        if (filters.street && s.spot.street !== filters.street) return false;
        if (filters.scenario && !(s.concepts?.scenarios || []).includes(filters.scenario))
          return false;
        if (filters.position && !(s.concepts?.positions || []).includes(filters.position))
          return false;
        if (filters.level && gradeDifficulty(s) !== filters.level) return false;
        return true;
      });

      // For review mode: prioritize FSRS-due concepts, then wrong answers
      if (m === "review") {
        const dueCards = getDueConcepts(50);
        const dueKeys = new Set(dueCards.map((c) => c.key));
        const progress = loadProgress();
        const wrongIds = new Set(
          Object.entries(progress.answered)
            .filter(([, v]) => !v.last.correct)
            .map(([k]) => k),
        );
        const dueScenes = pool.filter((s) => dueKeys.has(buildConceptKey(s)));
        const wrongScenes = pool.filter((s) => !dueKeys.has(buildConceptKey(s)) && wrongIds.has(s.scene_id));
        const rest = pool.filter((s) => !dueKeys.has(buildConceptKey(s)) && !wrongIds.has(s.scene_id));
        const shuffleTier = (arr: typeof pool) => {
          const a = [...arr];
          for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
          }
          return a;
        };
        pool = [...shuffleTier(dueScenes), ...shuffleTier(wrongScenes), ...shuffleTier(rest)];
      }

      // Shuffle / interleave
      let ids: string[];
      if (m === "mixed") {
        ids = interleaveScenes(
          pool.map((s) => ({ scene_id: s.scene_id, street: s.spot.street })),
        );
      } else if (m === "review") {
        ids = pool.map((s) => s.scene_id);
      } else {
        const shuffled = [...pool];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        ids = shuffled.map((s) => s.scene_id);
      }

      // 预置首题:seed 放队首,其余题从原池来(去重该场景),总量仍为 count
      const seedId = resolveSeed(seedSceneId);
      const rest = (seedId ? ids.filter((id) => id !== seedId) : ids).slice(
        0,
        seedId ? count - 1 : count,
      );
      const finalIds = seedId ? [seedId, ...rest] : rest;
      if (finalIds.length === 0) return;
      start(finalIds);
    },
    [allScenes, configure, start, resolveSeed],
  );

  const dueSceneIds = useMemo(() => {
    const dueKeys = new Set(
      Object.values(fsrsStore)
        .filter((c) => c.next_review <= dueNow)
        .map((c) => c.key),
    );
    if (dueKeys.size === 0) return [];
    const byConcept = new Map<string, string[]>();
    for (const s of allScenes) {
      if (!isTrainPoolScene(s)) continue;
      const key = buildConceptKey(s);
      if (!dueKeys.has(key)) continue;
      const list = byConcept.get(key) ?? [];
      if (list.length < PER_CONCEPT) list.push(s.scene_id);
      byConcept.set(key, list);
    }
    return [...byConcept.values()].flat().slice(0, REVIEW_CAP);
  }, [fsrsStore, allScenes, dueNow]);

  const handleStartDue = useCallback(
    (seedSceneId?: string) => {
      if (dueSceneIds.length === 0) return;
      const ids = [...dueSceneIds];
      for (let i = ids.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [ids[i], ids[j]] = [ids[j], ids[i]];
      }
      const seedId = resolveSeed(seedSceneId);
      const queue = seedId ? [seedId, ...ids.filter((id) => id !== seedId)] : ids;
      configure("review", {}, queue.length);
      start(queue);
    },
    [dueSceneIds, configure, start, resolveSeed],
  );

  const activeScenes = useMemo(
    () =>
      queue
        .map((id) => allScenes.find((s) => s.scene_id === id))
        .filter((s): s is PilotScene => s != null),
    [queue, allScenes],
  );

  const sceneById = useMemo(
    () => new Map(allScenes.map((s) => [s.scene_id, s])),
    [allScenes],
  );

  if (loading) {
    return (
      <DojoFrame>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
          <motion.span
            animate={reduce ? undefined : { opacity: [0.35, 1, 0.35] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="text-emerald-300/80"
          >
            <Spade className="size-6" />
          </motion.span>
          <p className="font-mono text-xs tabular-nums text-stone-500">
            正在摆牌桌…
          </p>
        </div>
      </DojoFrame>
    );
  }

  // 会话时长 = 各题实际作答时间之和(每题按 CAP_MS 封顶,防离线挂起污染)。
  // 不用 endedAt-startedAt 墙钟差:startedAt 经 persist 续练,种子/恢复会话会复用
  // 上次会话的陈旧 startedAt,墙钟差虚高(探针 M:1 题显示 11640 秒)。
  const doneDurationMs = results.reduce(
    (s, r) => s + Math.min(r.timeMs, CAP_MS),
    0,
  );

  if (phase === "setup") {
    return (
      <DojoFrame>
        <SetupPhase
          facets={facets}
          allScenes={allScenes}
          dueIds={dueSceneIds}
          onStart={handleStart}
          onStartDue={handleStartDue}
        />
      </DojoFrame>
    );
  }

  if (phase === "active" && activeScenes.length === 0) {
    return (
      <DojoFrame>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-sm text-stone-400">牌桌没摆起来</p>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-6 py-2.5 text-sm font-bold text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] transition-all duration-150 hover:bg-emerald-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
          >
            <RotateCcw className="size-4" />
            重新来过
          </button>
        </div>
      </DojoFrame>
    );
  }

  if (phase === "active") {
    return (
      <DojoFrame>
        <ActivePhase
          scenes={activeScenes}
          currentIndex={currentIndex}
          results={results}
          onAnswer={(correct, timeMs, chosenLabel) => {
            const scene = activeScenes[currentIndex];
            if (scene?.decision) {
              const conceptKey = buildConceptKey(scene);
              const before = getFsrsCard(conceptKey);
              const grade = gradeFromAnswer(correct, timeMs);
              const after = reviewConcept(conceptKey, grade);
              recordAnswer({
                sceneId: scene.scene_id,
                title: scene.spot.decision_point || scene.title,
                correct,
                timeMs,
                chosenLabel,
                correctLabel: scene.spot.recommendation || "",
                optionCount: scene.decision.options.length,
                correctCount: scene.decision.correct_ids.length,
                conceptKey,
                stabilityBefore: before?.stability ?? null,
                stabilityAfter: after.stability,
                nextReviewAt: after.next_review,
              });
              saveAnswer(scene.scene_id, chosenLabel, correct);
            }
          }}
          onNext={next}
          onQuit={finish}
        />
      </DojoFrame>
    );
  }

  // Done
  return (
    <DojoFrame>
      <DonePhase
        results={results}
        mode={mode}
        durationMs={doneDurationMs}
        sceneById={sceneById}
        onRestart={reset}
      />
    </DojoFrame>
  );
}
